import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { percentScale } from "@tylertech/common-components-module";
import { CommonDialogsService } from "@tylertech/common-dialogs-module";
import { isErroredExecutionResult, isHttpResponseError, NavigationService, ToastService } from "@tylertech/core-module";
import { IShellService } from "@tylertech/incode-module";
import { EmployeePhotoService } from "@tylertech/personnel-module";
import { EmailShare } from "@tylertech/shared-module";
import { ActionType, ExecutionResult, IAggregate, ICardAction, ICardData, ICardDataElement, ICardDataElementAction, PerformActionParams, PhotoViewModel, } from "@tylertech/tyler-api-types";
import { ChartConfiguration, ChartData, ChartType, Tick, TooltipItem } from "chart.js";
import { ActionReturn, IActionPerformedEventArgs } from "../../interfaces";
import { AggregateActionService, AggregateHelperService, AggregateService } from "../../services";

export type ChartConfigSansData<T extends ChartType = ChartType> = Omit<ChartConfiguration<T>, "data">;

@Component({
    selector: "card-elements",
    templateUrl: "./cardElements.tmpl.html",
    styleUrls: [
        "./cardElements.component.scss"
    ],
    standalone: false
})
export class CardElementComponent implements OnInit
{
    @Input() readonly aggregate: IAggregate;
    @Input() readonly cardData: ICardData;
    private _chartConfiguration: ChartConfigSansData;

    @Input("chartConfiguration")
    set boundChartConfig(value: Omit<ChartConfiguration, "data">)
    {
        this._chartConfiguration = value;
    }

    @Input() readonly color: string;

    @Output() readonly actionPerformedEvent = new EventEmitter<IActionPerformedEventArgs>();
    @Output() readonly reloadAggregate = new EventEmitter<IAggregate>();

    private User: string;
    private acceptedAction: ICardAction;
    chartConfig: ChartConfiguration;
    private chartData: ChartData;
    // chartId: string;
    isProcessing: boolean;

    /** @deprecated */ portrait: boolean;
    /** @deprecated */ hasPhoto: boolean;
    /** @deprecated */ photo: string;

    constructor(
        private readonly $toastService: ToastService,
        private readonly $navigationService: NavigationService,
        private readonly $shellService: IShellService,
        private readonly $commonDialogsService: CommonDialogsService,
        /**
         * TODO - This shouldn't depend on library/personnel, this package should be lower in the dep tree than the domain modules.
         * @deprecated
         */
        private readonly $employeePhotoService: EmployeePhotoService,
        private readonly $aggregateService: AggregateService,
        private readonly $aggregateHelperService: AggregateHelperService,
        private readonly $aggregateActionService: AggregateActionService)
    { }

    get hasChart(): boolean
    {
        const chartElement = this.getDataElementByLabel("HasChart");
        return !!chartElement && /true/i.test(chartElement.Value);
    }

    get aggregateProviderId(): string { return this.aggregate?.AggregateProviderId || ""; }
    get headerColor(): string { return this.cardData.HeaderColor; }
    get title(): string { return this.cardData?.Title || ""; }
    get elementsToShow(): ICardDataElement[] { return this.cardData.DataElements.filter(x => x.Visible && x.Type !== "Email" && this.hasValue(x)); }
    get emailElements(): ICardDataElement[] { return this.cardData.DataElements.filter(x => x.Visible && x.Type === "Email"); }

    get hasCardActions(): boolean { return this.cardData.CardActions?.length > 0; }

    /** @deprecated */ get photoUrl(): string { return `data:image/jpeg;base64,${this.photo}`; }

    ngOnInit(): void
    {
        if (this.getField("EmployeeId"))
        {
            this.getPortrait();
        }
        this.loadActions();

        if (this.hasChart)
        {
            this.setupChart();
        }
    }

    hasIcon(cardDataElement: ICardDataElement): boolean
    {
        return !!cardDataElement.Icon;
    }

    isCurrency(dataElement: ICardDataElement): boolean
    {
        return dataElement.Type === "currency";
    }

    private actionClicked(action: string, $event?: MouseEvent): void
    {
        if ($event !== undefined)
        {
            $event.stopPropagation();
        }
        if (action === "share")
        {
            const email = this.getField("Email");
            const emailSubject = this.getField("EmailSubject");
            const emailBody = this.getField("EmailBody");

            EmailShare.send({
                recipient: email || "",
                subject: emailSubject || this.aggregate.Name,
                message: emailBody || ""
            });
        }
        else
        {
            if (!this.checkPrerequisites(action)) return;
            this.isProcessing = true;
        }
    }

    private hasActionItem(dataElement: ICardDataElement): boolean
    {
        return !!dataElement.ActionItems;
    }

    cardActionClicked(action: ICardAction): void
    {
        this.isProcessing = true;
        const self = this;
        this.$aggregateActionService.processAction(this.aggregate, this.cardData, action)
            .then((result: ActionReturn | boolean) =>
            {
                if (typeof result === "boolean")
                {
                    return result;
                }

                if (action.actionType === ActionType.Server
                    || action.actionType === ActionType.Intent
                    || (result.action && result.action.actionType === ActionType.Server)
                    || (action.actionType === ActionType.Metadata))
                {
                    return true;
                }
            })
            .then((result: boolean) =>
            {
                if (!result)
                {
                    return;
                }
                self.actionPerformedEvent.emit(
                    {
                        card: self.cardData,
                        action: "",
                        replacement: null,
                        cardAction: action,
                    });

                if (action.ReloadOnSuccess)
                {
                    self.reloadAggregate.emit(self.aggregate);
                }
            })
            .finally(() =>
            {
                this.isProcessing = false;
            });
    }

    private checkForComment(action: string): boolean
    {
        const required = this.getField(`${action}.RequiresComment`);
        if (!required)
        {
            return true;
        }

        const comment = this.getField("Comment");
        if (comment)
        {
            return true;
        }

        const title = this.getField(`${action}.CommentLabel`) || "Comment:";
        const padding = new Array(50).join(" &nbsp; ");
        this.$commonDialogsService.showHtmlPrompt(padding, title)
            .then((text: string) =>
            {
                this.getDataElementByLabel("Comment").Value = text;
                this.actionClicked(action);
            });

        return false;
    }

    private checkPrerequisites(action: string): boolean
    {
        return this.checkForComment(action);
    }

    private getDataElementByLabel(label: string): ICardDataElement | undefined
    {
        return this.$aggregateHelperService.getDataElementByLabel(label, this.cardData.DataElements);
    }

    private getField(fieldName: string): string
    {
        return this.$aggregateHelperService.getFieldValue(this.cardData, fieldName);
    }

    getIconStyle(color: string): Record<string, string> | undefined
    {
        if (color !== "default")
        {
            return {
                color: color
            };
        }
    }

    /**
     * @deprecated - this should be a data field add on the back end... not fetched here...
     */
    private getPortrait(): void
    {
        this.portrait = true;
        this.$employeePhotoService.GetPhoto(this.getField("EmployeeId"))
            .then((executionResult: ExecutionResult<PhotoViewModel>) =>
            {
                const result = executionResult.Result;
                if (result?.Photo)
                {
                    this.photo = result.Photo;
                    this.hasPhoto = true;
                }
            });
    }

    hasValue(dataElement: ICardDataElement): boolean
    {
        return !!dataElement.Value;
    }

    isBadgeButton(action: ICardAction): boolean
    {
        return action.actionType === ActionType.Notes || action.actionType === ActionType.Attachments;
    }

    isIconButton(action: ICardAction): boolean
    {
        return !!action.icon && !this.isBadgeButton(action);
    }

    isTextButton(action: ICardAction): boolean
    {
        return !this.isBadgeButton(action) && !this.isIconButton(action);
    }

    private loadActions(): void
    {
        if (!this.cardData.CardActions) return;
        this.cardData.CardActions.forEach(action =>
        {
            this.$aggregateActionService.loadAction(this.aggregate, this.cardData, action);
        });
    }

    openTypeCommand(dataElement: ICardDataElement): void
    {
        const functionName: string = dataElement.ActionItems.ActionValue;
        const id: string = dataElement.ActionItems.ActionId;
        const appName: string | undefined = dataElement.ActionItems.ActionServerUrl;

        if (!this.$shellService.isEmbedded() && appName)
        {
            this.$navigationService.navigateToApp(appName);
            return;
        }

        // TODO - Why are we talking about an account manager on a general action launcher?????
        this.$shellService.openShellFunction(functionName, id, true, "Failed to launch account manager: ")
            .then((result: ExecutionResult<unknown>) =>
            {
                if (result.Result !== "Accepted")
                {
                    return;
                }

                this.performServerCardAction(this.acceptedAction);
            });
    }

    performMetadataAction(elementAction: ICardDataElementAction): void
    {
        const action = <ICardAction>{
            actionType: ActionType.Metadata,
            content: elementAction.ActionValue,
        };
        this.$aggregateActionService.processAction(this.aggregate, this.cardData, action);
    }

    private performServerCardAction(action: ICardAction): Promise<unknown>
    {
        this.isProcessing = true;
        const model: PerformActionParams = {
            Aggregate: this.aggregate,
            Card: this.cardData,
            CardAction: action,
            UserId: this.User,
            Action: undefined,
            AggregateProviderId: undefined,
            CardProviderId: undefined,
            ConfirmationQuestions: undefined
        };

        return this.$aggregateService.PerformCardAction(model)
            .then(
                (x: ExecutionResult<ICardData>) =>
                {
                    if (x.Success)
                    {
                        return x.Result;
                    }

                    return this.$commonDialogsService.showAlertHtmlMessage(
                        x.ErrorMessage || x.ValidationMessage,
                        "Error Performing Action")
                        .then(
                            () =>
                            {
                                this.reloadAggregate.emit(this.aggregate);
                                return x.Result;
                            });
                })
            .then(
                (result: ICardData) =>
                {
                    if (action.actionType === ActionType.ServerUrl)
                    {
                        const url = this.$aggregateHelperService.getDataElementByLabel("Url", result.DataElements);
                        if (url.Value)
                        {
                            if (url.Value.substring(0, 1) === "/")
                            {
                                this.$navigationService.navigateToApp(url.Value);
                            }
                            else
                            {
                                window.location.href = url.Value;
                            }
                            return;
                        }
                    }

                    this.actionPerformedEvent.emit(<IActionPerformedEventArgs>{
                        card: this.cardData,
                        action: "",
                        replacement: result,
                        cardAction: action,
                    });
                },
                (error: unknown) =>
                {
                    if (isErroredExecutionResult(error))
                    {
                        this.$toastService.showToast(error.ErrorMessage);
                    }
                    else if (isHttpResponseError(error))
                    {
                        this.$toastService.showToast(error.error
                            .Message);
                    }
                    else
                    {
                        this.$toastService.showToast("An Error occurred.");
                        console.error(error, new Error("An Error occurred."));
                    }
                })
            .finally(() =>
            {
                this.isProcessing = false;
            });
    }

    private getDefaultChartConfiguration(): Omit<ChartConfiguration<"bar">, "data">
    {
        return {
            type: "bar",
            options: {
                responsive: true,
                maintainAspectRatio: false,
                indexAxis: "y",
                plugins: {
                    tooltip: {
                        mode: 'nearest',
                        intersect: true,
                        callbacks: {
                            title: () => null,
                            label: (tooltipItem: TooltipItem<"bar">): string | string[] =>
                            {
                                const itemLabel = tooltipItem.chart.data.labels[tooltipItem.dataIndex] as Array<string>;
                                const dataset = tooltipItem.chart.data.datasets[tooltipItem.datasetIndex];
                                const label = itemLabel
                                    ? itemLabel[tooltipItem.dataIndex]
                                    || (dataset.label ? dataset.label : "")
                                    : "";

                                const value = dataset.data[tooltipItem.dataIndex] as string | number;

                                const valueString = percentScale(
                                    typeof value === "number"
                                        ? value.toFixed(2)
                                        : value);

                                return label.length > 0
                                    ? `${label}: ${valueString}`
                                    : valueString;
                            },
                        },
                        backgroundColor: '#ffffff',
                        borderColor: '#767676',
                        borderWidth: 1
                    },
                    legend: { display: false },
                },
                scales: {
                    x: {
                        stacked: true,
                        beginAtZero: true,
                        min: 0,
                        max: 100,
                        ticks: {
                            maxTicksLimit: 5,
                            stepSize: 25,
                            callback: (value: number | string, _index: number, ticks: Tick[]): string | number =>
                            {
                                return percentScale(value);
                            },
                        },
                    },
                    y: { stacked: true, },
                },
            },
        };
    }

    private setupChart(): void
    {
        const colors = this.getDataElementByLabel("ChartColor").Value.split(",");
        const labels = this.getDataElementByLabel("ChartLabel").Value.split(",");
        const data = this.getDataElementByLabel("ChartData")
            .Value.split(",")
            .map((x) => parseFloat(x));

        this.chartData = {
            labels: labels,
            datasets: data.map(
                (x: number, i: number) =>
                {
                    return {
                        data: [x || 0],
                        label: labels[i],
                        backgroundColor: colors[i] || "#3F51B5",
                    };
                })
        };

        if (!this._chartConfiguration)
        {
            // TODO - why are we needing a chartConfig if none was supplied
            this._chartConfiguration = this.getDefaultChartConfiguration() as ChartConfiguration;
        }

        // TODO - why are we overriding what we were given...
        if (this._chartConfiguration.type !== "bar"
            && !!this._chartConfiguration.options.indexAxis)
        {
            this.chartData.labels = [""];
        }

        this.chartConfig = {
            ...this._chartConfiguration,
            data: this.chartData,
        };
    }

    showField(dataElement: ICardDataElement): boolean
    {
        return dataElement.Type !== "currency"
            && !this.hasIcon(dataElement)
            && this.showLink(dataElement, true, true)
            && !this.showMetadataLink(dataElement);
    }

    showLink(dataElement: ICardDataElement, isServer: boolean, isUrl: boolean = false): boolean
    {
        if (!this.hasActionItem(dataElement))
        {
            return isServer && isUrl;
        }

        if ((dataElement.ActionItems.ActionType === ActionType.Server
            && this.$shellService.isEmbedded())
            || dataElement.ActionItems.ActionServerUrl)
        {
            return isServer && !isUrl;
        }
        else if (dataElement.ActionItems.ActionType === ActionType.Url)
        {
            return !isServer && isUrl;
        }
        return isServer && isUrl;
    }

    showMetadataLink(dataElement: ICardDataElement): boolean
    {
        return this.hasActionItem(dataElement) && dataElement.ActionItems.ActionType === ActionType.Metadata;
    }

    usesIconSpan(value: string): boolean
    {
        return value.startsWith("icon-");
    }

    getIconSpanClass(dataElement: ICardDataElement): Array<string>
    {
        return [
            dataElement.Icon
        ];
    }

    getCardActionAriaLabel(action: ICardAction): string
    {
        return action.label;
    }

    getCardActionTelemetryLabel(action: ICardAction): string
    {
        return `${this.aggregateProviderId}-${action.label}`;
    }
}
